import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Menu,
  X,
  ArrowRight,
  Search,
  Layers,
  Workflow,
  History,
  ShieldCheck,
  Bell,
  Users,
  FileText,
  PenSquare,
  Smartphone,
  BarChart3,
  Lock,
  Signature,
  BookOpen,
  FolderTree,
  Compass,
  Zap,
  KeyRound,
  Fingerprint,
  ServerCog,
  ScrollText,
  LinkedinIcon,
  Check,
  Factory,
  Stethoscope,
  Landmark,
  ShoppingBag,
  Truck,
  Fuel,
  ChevronRight,
  Mail,
  Phone,
  CreditCard,
  LayoutGrid,
  Building2,
  CircleDollarSign,
  Rocket,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { AmbientBackground } from "@/components/AmbientBackground";
import { StatusBadge } from "@/components/StatusBadge";

/* -------------------- helpers -------------------- */

// IntersectionObserver-based "reveal" — respects prefers-reduced-motion
function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setSeen(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
}

function Reveal({ children, delay = 0, className = "", as: Tag = "div" }) {
  const [ref, seen] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`transition-all duration-700 ease-out will-change-transform ${
        seen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

const PRICING_PLANS = [
  {
    id: "starter",
    tabLabel: "Starter",
    icon: Rocket,
    name: "Standard Starter",
    blurb: "Perfect for small teams getting started with structured SOP drafting.",
    price: "$0",
    cadence: "Free trial",
    cta: "Start drafting free",
    featured: false,
    features: [
      "Standard SOP library & folders",
      "Full-text search and tags",
      "E-signature acknowledgements",
      "PWA standalone mobile view",
    ],
  },
  {
    id: "enterprise",
    tabLabel: "Enterprise",
    icon: CreditCard,
    name: "Enterprise Pro Bundle",
    blurb: "Unlock connectors, IoT triggers, WhatsApp alerts, and crypto audit seals.",
    price: "$29",
    cadence: "/ month",
    cta: "Subscribe & unlock all",
    featured: true,
    features: [
      "WhatsApp Business bot integrations",
      "ESP32 hardware IoT connector",
      "SAP webhook / OData bridge",
      "SHA-256 crypto seal verification",
      "Custom AI LLM settings",
    ],
  },
];

function PricingPanel({ onCta }) {
  return (
    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-testid="landing-pricing-panel">
      {PRICING_PLANS.map((plan, i) => {
        const PlanIcon = plan.icon;
        return (
          <Reveal key={plan.id} delay={50 + i * 100}>
            <div
              className={[
                "rounded-[32px] p-8 h-full flex flex-col justify-between relative overflow-hidden",
                plan.featured
                  ? "glass-strong border border-cyan-500/30"
                  : "glass border border-white/5",
              ].join(" ")}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.featured && (
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl animate-pulse pointer-events-none" />
              )}
              <div className="relative">
                {plan.featured && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-[10px] text-cyan-300 font-bold uppercase tracking-wider mb-4">
                    <Sparkles size={10} /> Best value
                  </div>
                )}
                <div className="flex items-center gap-2.5">
                  <div
                    className={[
                      "w-10 h-10 rounded-2xl flex items-center justify-center shrink-0",
                      plan.featured ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/20" : "glass text-[#9AA4B2]",
                    ].join(" ")}
                  >
                    <PlanIcon size={18} aria-hidden />
                  </div>
                  <h3 className="font-display text-lg text-white font-semibold">{plan.name}</h3>
                </div>
                <p className="text-xs text-[#9AA4B2] mt-2">{plan.blurb}</p>
                <div className="my-6">
                  <span className="text-3xl font-display font-bold text-white">{plan.price}</span>
                  <span className="text-xs text-[#9AA4B2] ml-1">{plan.cadence}</span>
                </div>
                <ul className="space-y-3.5 border-t border-white/5 pt-6 text-xs text-white/80">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className={[
                        "flex items-center gap-2.5",
                        plan.featured ? "text-cyan-100/90" : "",
                      ].join(" ")}
                    >
                      <Check
                        size={14}
                        className={plan.featured ? "text-cyan-300 shrink-0" : "text-emerald-400 shrink-0"}
                      />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={onCta}
                className={[
                  "w-full mt-8 py-3 rounded-full text-xs font-semibold transition",
                  plan.featured
                    ? "bg-gradient-to-br from-[#2563EB] to-[#4F46E5] text-white shadow-lg shadow-indigo-500/20 hover:opacity-95 font-bold"
                    : "bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white",
                ].join(" ")}
              >
                {plan.cta}
              </button>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

/* -------------------- nav -------------------- */

function TopNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#features", label: "Features", icon: LayoutGrid, iconClass: "text-indigo-300" },
    { href: "#pricing", label: "Pricing", icon: CircleDollarSign, iconClass: "text-amber-300" },
    { href: "#solutions", label: "Solutions", icon: Compass, iconClass: "text-cyan-300" },
    { href: "#compliance", label: "Compliance", icon: ShieldCheck, iconClass: "text-emerald-300" },
    { href: "#about", label: "About", icon: Building2, iconClass: "text-sky-300" },
  ];

  return (
    <header
      data-testid="landing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div
          className={`glass rounded-full flex items-center gap-4 px-4 lg:px-6 py-2.5 transition-all ${
            scrolled ? "shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)]" : ""
          }`}
        >
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#2563EB] via-[#4F46E5] to-[#06B6D4] flex items-center justify-center shadow-[0_0_18px_-2px_rgba(79,70,229,0.7)]">
              <Sparkles size={17} className="text-white" strokeWidth={2.2} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight text-white">SOPFlow</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-3 flex-1" aria-label="Primary">
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <a
                  key={l.href}
                  href={l.href}
                  className="group inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#9AA4B2] hover:text-white rounded-full hover:bg-white/[0.04] transition"
                >
                  <Icon size={15} className={`shrink-0 ${l.iconClass}`} aria-hidden />
                  <span className="font-medium">{l.label}</span>
                </a>
              );
            })}
          </nav>

          <div className="hidden md:flex ml-auto items-center gap-2">
            <button
              onClick={() => navigate("/signin")}
              data-testid="nav-signin-btn"
              className="btn-primary-glow rounded-full px-5 py-2 text-sm font-medium flex items-center gap-1.5"
            >
              Sign In <ArrowRight size={14} />
            </button>
          </div>

          {/* mobile */}
          <button
            className="md:hidden ml-auto w-9 h-9 rounded-full glass flex items-center justify-center text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden mt-2 glass rounded-3xl p-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2.5 px-3 py-2.5 rounded-2xl text-sm text-[#9AA4B2] hover:text-white hover:bg-white/[0.04]"
                >
                  <Icon size={16} className={`shrink-0 ${l.iconClass}`} aria-hidden />
                  <span className="font-medium">{l.label}</span>
                </a>
              );
            })}
            <button
              onClick={() => navigate("/signin")}
              className="mt-2 btn-primary-glow rounded-full py-2.5 text-sm font-medium"
            >
              Sign In
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

/* -------------------- hero mock preview -------------------- */

function HeroPreview() {
  return (
    <div className="relative w-full aspect-[4/3.4] max-w-[640px] mx-auto" data-testid="hero-preview">
      {/* Halo */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-8 -left-6 w-56 h-56 rounded-full bg-[#2563EB]/50 blur-[100px]" />
        <div className="absolute -bottom-6 -right-6 w-72 h-72 rounded-full bg-[#06B6D4]/40 blur-[110px]" />
      </div>

      {/* Base card */}
      <div className="glass-strong rounded-3xl p-5 h-full flex flex-col animate-[floaty_10s_ease-in-out_infinite]">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
          <div className="ml-3 flex-1 h-7 rounded-full bg-white/[0.04] border border-white/10 flex items-center gap-2 px-3">
            <Search size={12} className="text-[#5B6472]" />
            <span className="text-[11px] text-[#5B6472]">Ask anything… find an SOP</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-3">
          {[
            { label: "SOPs", value: 128, color: "text-indigo-300" },
            { label: "Approved", value: 96, color: "text-emerald-300" },
            { label: "In review", value: 12, color: "text-amber-300" },
          ].map((s) => (
            <div key={s.label} className="glass rounded-2xl p-3">
              <div className="text-[9px] uppercase tracking-widest text-[#9AA4B2]">{s.label}</div>
              <div className={`font-display text-xl mt-0.5 ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="glass rounded-2xl p-3 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-white/80">Recent SOPs</div>
            <div className="text-[10px] text-[#5B6472]">v · owner · dept</div>
          </div>
          <div className="space-y-1.5 overflow-hidden">
            {[
              { t: "Warehouse Receiving", v: "2.1", d: "Operations", s: "approved" },
              { t: "Incident Response Runbook", v: "1.3", d: "IT", s: "approved" },
              { t: "QA Non-Conformance", v: "1.2", d: "Operations", s: "in_review" },
              { t: "Vendor Payment Flow", v: "0.1", d: "Finance", s: "draft" },
            ].map((r) => (
              <div
                key={r.t}
                className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/[0.04]"
              >
                <div className="w-6 h-6 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-indigo-300">
                  <BookOpen size={11} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] text-white truncate">{r.t}</div>
                  <div className="text-[9px] text-[#5B6472]">v{r.v} · {r.d}</div>
                </div>
                <StatusBadge status={r.s} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating comp: e-sign */}
      <div
        className="absolute -left-6 bottom-14 glass-strong rounded-2xl p-3.5 w-56 animate-[floaty2_9s_ease-in-out_infinite]"
      >
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1 flex items-center gap-1">
          <Signature size={11} /> E-signature captured
        </div>
        <div className="text-white text-sm font-medium">Priya Chandra</div>
        <div
          className="text-lg text-white mt-1"
          style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive" }}
        >
          Priya Chandra
        </div>
        <div className="text-[10px] text-[#5B6472] mt-1">Signed · 12:04 · 10.4.1.221</div>
      </div>

      {/* Floating comp: coverage ring */}
      <div className="absolute -right-4 top-10 glass-strong rounded-2xl p-3.5 w-48 animate-[floaty_11s_ease-in-out_infinite]">
        <div className="text-[10px] uppercase tracking-widest text-[#9AA4B2]">Coverage</div>
        <div className="mt-1 flex items-end gap-2">
          <div className="font-display text-3xl text-white">94%</div>
          <div className="text-[10px] text-[#5B6472] mb-1">432 / 460 acks</div>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
          <div className="h-full w-[94%] bg-gradient-to-r from-emerald-400 to-cyan-400" style={{ boxShadow: "0 0 12px rgba(16,185,129,0.6)" }} />
        </div>
      </div>
    </div>
  );
}

/* -------------------- feature card primitives -------------------- */

function ProblemCard({ icon: Icon, title, desc, delay = 0, tint = "indigo" }) {
  const tints = {
    indigo: "from-indigo-500/25 text-indigo-300",
    cyan: "from-cyan-500/25 text-cyan-300",
    amber: "from-amber-500/25 text-amber-300",
    emerald: "from-emerald-500/25 text-emerald-300",
    rose: "from-rose-500/25 text-rose-300",
  }[tint];
  return (
    <Reveal delay={delay}>
      <div className="glass rounded-3xl p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_-30px_rgba(79,70,229,0.55)]">
        <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${tints} to-transparent border border-white/10 flex items-center justify-center mb-4`}>
          <Icon size={20} strokeWidth={1.8} />
        </div>
        <h3 className="font-display text-lg text-white leading-snug">{title}</h3>
        <p className="mt-1.5 text-sm text-[#9AA4B2] leading-relaxed">{desc}</p>
      </div>
    </Reveal>
  );
}

function FeatureItem({ icon: Icon, title, desc, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <div className="p-5 rounded-3xl bg-white/[0.02] border border-white/5 h-full transition-all duration-300 hover:bg-white/[0.05] hover:border-white/10">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#4F46E5]/25 to-transparent border border-white/10 flex items-center justify-center text-indigo-200 mb-3">
          <Icon size={18} strokeWidth={1.8} />
        </div>
        <div className="font-display text-white text-base">{title}</div>
        <div className="text-sm text-[#9AA4B2] mt-1 leading-relaxed">{desc}</div>
      </div>
    </Reveal>
  );
}

/* -------------------- workflow steps -------------------- */

function WorkflowStep({ icon: Icon, label, delay, isLast = false }) {
  return (
    <Reveal delay={delay} className="shrink-0">
      <div className="flex items-start">
        <div className="flex flex-col items-center w-[104px]">
          <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center text-white bg-gradient-to-br from-white/[0.06] to-transparent border-white/15">
            <Icon size={22} />
          </div>
          <div className="mt-2 text-xs text-white text-center leading-snug min-h-[2rem] px-1">
            {label}
          </div>
        </div>
        {/* Arrow centered on the icon row (h-14), not the full column including label */}
        {!isLast && (
          <div className="hidden md:flex h-14 items-center px-1 lg:px-2" aria-hidden>
            <div className="relative w-10 lg:w-12 h-px bg-gradient-to-r from-indigo-400/60 to-cyan-400/60">
              <ChevronRight
                size={14}
                className="absolute -right-1.5 top-1/2 -translate-y-1/2 text-cyan-400/90"
                strokeWidth={2.2}
              />
            </div>
          </div>
        )}
      </div>
    </Reveal>
  );
}

/* -------------------- dashboard mocks (visual only) -------------------- */

function ChampionDashboardMock() {
  return (
    <div className="glass-strong rounded-3xl p-4 h-full">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300">Process Champion</div>
          <div className="font-display text-white text-sm">My drafts</div>
        </div>
        <div className="px-3 py-1 rounded-full text-[10px] btn-primary-glow">+ New SOP</div>
      </div>
      <div className="space-y-2">
        {[
          ["Vendor Payment Approval", "draft", "0.2"],
          ["QA NCR Handling", "in_review", "1.2"],
          ["Grievance Redressal", "draft", "0.1"],
        ].map(([t, s, v]) => (
          <div key={t} className="flex items-center gap-2 p-2 rounded-xl bg-white/[0.03]">
            <PenSquare size={12} className="text-indigo-300" />
            <div className="flex-1 min-w-0 text-[11px] text-white truncate">{t}</div>
            <span className="text-[9px] text-[#5B6472]">v{v}</span>
            <StatusBadge status={s} />
          </div>
        ))}
      </div>
    </div>
  );
}

function OwnerDashboardMock() {
  return (
    <div className="glass-strong rounded-3xl p-4 h-full">
      <div className="text-[10px] uppercase tracking-widest text-amber-300">Process Owner</div>
      <div className="font-display text-white text-sm mb-3">Approvals inbox</div>
      <div className="space-y-2">
        {[
          ["Data Backup & Restore", "IT · 2d ago"],
          ["QA Non-Conformance", "Ops · 4h ago"],
        ].map(([t, m]) => (
          <div key={t} className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="text-[11px] text-white">{t}</div>
            <div className="text-[9px] text-[#5B6472] mt-0.5">{m}</div>
            <div className="mt-2 flex gap-1.5">
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300">Approve</span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[#9AA4B2]">Comment</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LibraryMock() {
  return (
    <div className="glass-strong rounded-3xl p-4 h-full">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300">Employee</div>
      <div className="font-display text-white text-sm mb-3">SOP library</div>
      <div className="grid grid-cols-2 gap-2">
        {["Warehouse Receiving", "Incident Response", "Expense Policy", "Onboarding"].map((t) => (
          <div key={t} className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="w-6 h-6 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-indigo-300 mb-1">
              <BookOpen size={11} />
            </div>
            <div className="text-[10.5px] text-white leading-tight">{t}</div>
            <div className="mt-1"><StatusBadge status="approved" /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComplianceMock() {
  return (
    <div className="glass-strong rounded-3xl p-4 h-full">
      <div className="text-[10px] uppercase tracking-widest text-rose-300">Compliance</div>
      <div className="flex items-end gap-2">
        <div className="font-display text-3xl text-white">92%</div>
        <div className="text-[9px] text-[#5B6472] mb-1.5">org-wide coverage</div>
      </div>
      <div className="mt-2 space-y-2">
        {[
          ["Receiving", 96, "emerald"],
          ["IT Response", 88, "cyan"],
          ["Cold Storage", 61, "amber"],
        ].map(([t, p, c]) => (
          <div key={t}>
            <div className="flex justify-between text-[10px] text-[#9AA4B2]">
              <span>{t}</span>
              <span className="text-white">{p}%</span>
            </div>
            <div className="h-1 rounded-full bg-white/[0.04] mt-0.5 overflow-hidden">
              <div
                className={`h-full ${
                  c === "emerald"
                    ? "bg-gradient-to-r from-emerald-400 to-cyan-400"
                    : c === "cyan"
                    ? "bg-gradient-to-r from-cyan-400 to-indigo-400"
                    : "bg-gradient-to-r from-amber-400 to-rose-400"
                }`}
                style={{ width: `${p}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------- page -------------------- */

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // If already logged in, quietly send them to the app.
  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      <AmbientBackground />
      <TopNav />

      {/* Inline keyframes for floaty motion (respects reduced motion via CSS) */}
      <style>{`
        @keyframes floaty { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        @keyframes floaty2 { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-14px); } }
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[floaty_10s_ease-in-out_infinite\\],
          .animate-\\[floaty_11s_ease-in-out_infinite\\],
          .animate-\\[floaty2_9s_ease-in-out_infinite\\] { animation: none !important; }
        }
      `}</style>

      {/* -------- HERO -------- */}
      <section className="relative pt-32 lg:pt-40 pb-20" data-testid="landing-hero">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass text-[11px] uppercase tracking-widest text-cyan-300">
                <Sparkles size={12} /> Enterprise SOP platform · 2026
              </div>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white">
                SOPs your team can actually{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7DD3FC] via-[#818CF8] to-[#C4B5FD]">
                  find, trust, and follow.
                </span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-5 text-[#9AA4B2] text-base sm:text-lg max-w-xl leading-relaxed">
                Modern SOP management for enterprises. Centralize documentation, automate approvals, ensure compliance, and keep every team aligned — from the warehouse floor to the C-suite.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate("/signin")}
                  data-testid="hero-get-started-btn"
                  className="btn-primary-glow rounded-full px-6 py-3.5 text-base font-medium flex items-center gap-2"
                >
                  Get Started <ArrowRight size={16} />
                </button>
                <button
                  onClick={() => navigate("/signin")}
                  data-testid="hero-signin-btn"
                  className="btn-ghost-glass rounded-full px-6 py-3.5 text-base font-medium"
                >
                  Sign In
                </button>
              </div>
            </Reveal>
            <Reveal delay={280}>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#5B6472]">
                {[
                  "Microsoft Entra ID ready",
                  "ISO 9001 / 27001 audit trail",
                  "SharePoint bridge",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-1.5">
                    <Check size={12} className="text-emerald-400" /> {t}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={200} className="lg:pl-6">
            <HeroPreview />
          </Reveal>
        </div>
      </section>

      {/* -------- TRUSTED BY -------- */}
      <section className="py-10" data-testid="landing-trusted">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center text-[11px] uppercase tracking-[0.35em] text-[#5B6472]">
              Trusted by teams across
            </div>
          </Reveal>
          <div className="mt-6 grid grid-cols-3 md:grid-cols-6 gap-4">
            {[
              { name: "Manufacturing", icon: Factory },
              { name: "Healthcare", icon: Stethoscope },
              { name: "Finance", icon: Landmark },
              { name: "Retail", icon: ShoppingBag },
              { name: "Logistics", icon: Truck },
              { name: "Energy", icon: Fuel },
            ].map((v, i) => (
              <Reveal key={v.name} delay={i * 60}>
                <div className="glass rounded-2xl px-4 py-3.5 flex items-center gap-2 justify-center text-[#9AA4B2] hover:text-white transition">
                  <v.icon size={16} strokeWidth={1.6} />
                  <span className="text-xs font-medium tracking-wide">{v.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* -------- PROBLEMS -------- */}
      <section id="solutions" className="py-24" data-testid="landing-problems">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300">Problems we solve</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white leading-tight tracking-tight">
                Five reasons SOPs quietly fail — and how SOPFlow fixes each one.
              </h2>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ProblemCard
              tint="cyan"
              icon={Search}
              title="&ldquo;I can&apos;t find it when I need it.&rdquo;"
              desc="Scattered drives, email threads, and paper binders. SOPFlow gives you one searchable library on any device."
              delay={40}
            />
            <ProblemCard
              tint="indigo"
              icon={History}
              title="&ldquo;Which version is correct?&rdquo;"
              desc="Auto-numbered versions, previous revisions superseded, and only the current approved SOP shown by default."
              delay={100}
            />
            <ProblemCard
              tint="amber"
              icon={Bell}
              title="&ldquo;Nobody told me it changed.&rdquo;"
              desc="Every submission, change request, and publication triggers targeted, trackable notifications."
              delay={160}
            />
            <ProblemCard
              tint="emerald"
              icon={ShieldCheck}
              title="&ldquo;How do we prove compliance?&rdquo;"
              desc="E-signed acknowledgements, timestamped audit trails, and one-click reports for ISO 9001 / 27001."
              delay={220}
            />
            <ProblemCard
              tint="rose"
              icon={Users}
              title="&ldquo;Who owns this now?&rdquo;"
              desc="Every SOP has a named owner and next-review date. Reassign in one click when someone moves teams."
              delay={280}
            />
          </div>
        </div>
      </section>

      {/* -------- FEATURES -------- */}
      <section id="features" className="py-24" data-testid="landing-features">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div className="max-w-2xl">
                <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300">Everything you need</div>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white leading-tight tracking-tight">
                  A full SOP toolkit — engineered like Linear, secured like a bank.
                </h2>
              </div>
              <div className="text-sm text-[#9AA4B2] max-w-md">
                Twelve first-class capabilities. Zero busywork.
              </div>
            </div>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {[
              [BookOpen, "SOP Library", "One searchable home for every procedure with tags, folders and departments."],
              [PenSquare, "Rich Editor", "Author with sections, lists and inline diagrams — with autosave built in."],
              [Workflow, "Flowchart Designer", "Drag-and-drop nodes, decisions and connectors — right inside the SOP."],
              [History, "Version Control", "Auto-numbered versions with changelogs and previous revisions preserved."],
              [Check, "Approval Workflow", "Multi-approver sign-off with sequential or parallel routing."],
              [Signature, "E-Signatures", "Typed name + timestamp + device metadata captured immutably."],
              [Search, "Smart Search", "Full-text search across titles, tags and descriptions."],
              [Bell, "Notifications", "In-app alerts for submissions, changes-requested, publications and reviews."],
              [BarChart3, "Compliance Dashboard", "Per-SOP coverage %, exportable to CSV for auditors."],
              [Smartphone, "Mobile Access", "Every screen works on a phone — glass tab bar included."],
              [ScrollText, "Audit Reports", "Immutable acknowledgement log ready for ISO 9001 / 27001."],
              [KeyRound, "Role-Based Access", "Champion, Owner, Reader and Admin — permissions all the way down."],
            ].map(([Ic, t, d], i) => (
              <FeatureItem key={t} icon={Ic} title={t} desc={d} delay={i * 40} />
            ))}
          </div>
        </div>
      </section>

      {/* -------- WORKFLOW -------- */}
      <section className="py-24" data-testid="landing-workflow">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300">The workflow</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white leading-tight tracking-tight">
                Draft → Review → Approve → Publish → Acknowledge → Audit.
              </h2>
            </div>
          </Reveal>

          <div className="mt-12 flex flex-col md:flex-row md:items-start justify-center gap-4 md:gap-0 overflow-x-auto pb-2">
            {[
              [PenSquare, "Create SOP"],
              [Users, "Review"],
              [ShieldCheck, "Approve"],
              [Zap, "Publish"],
              [BookOpen, "Employees Read"],
              [BarChart3, "Compliance Report"],
            ].map(([Ic, l], i, arr) => (
              <React.Fragment key={l}>
                <WorkflowStep icon={Ic} label={l} delay={i * 90} isLast={i === arr.length - 1} />
                {i !== arr.length - 1 && (
                  <div className="md:hidden flex justify-center text-cyan-300 py-1" aria-hidden>
                    <ChevronRight size={16} className="rotate-90" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* -------- DASHBOARD PREVIEW -------- */}
      <section id="about" className="py-24" data-testid="landing-dash-preview">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300">One product, four views</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white leading-tight tracking-tight">
                Every role gets a purpose-built home.
              </h2>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Reveal delay={40}><ChampionDashboardMock /></Reveal>
            <Reveal delay={100}><OwnerDashboardMock /></Reveal>
            <Reveal delay={160}><LibraryMock /></Reveal>
            <Reveal delay={220}><ComplianceMock /></Reveal>
          </div>
        </div>
      </section>

      {/* -------- WHY -------- */}
      <section className="py-24" data-testid="landing-why">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300">Why SOPFlow</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white leading-tight tracking-tight">
                Three commitments we hold ourselves to.
              </h2>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              {
                icon: Compass,
                title: "Single source of truth",
                desc: "Users always land on the current approved SOP. Superseded versions are archived, never accidentally shared.",
                tint: "cyan",
              },
              {
                icon: ShieldCheck,
                title: "Enterprise compliance",
                desc: "Every acknowledgement is signed and timestamped with device metadata. Auditors get a clean CSV in one click.",
                tint: "emerald",
              },
              {
                icon: Users,
                title: "Clear ownership",
                desc: "Every SOP has an assigned owner and next-review date. Automated reminders when reviews come due.",
                tint: "indigo",
              },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="glass rounded-3xl p-7 h-full transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_25px_60px_-30px_rgba(6,182,212,0.4)]">
                  <div className={`w-12 h-12 rounded-2xl border border-white/10 flex items-center justify-center mb-4 ${
                    c.tint === "cyan" ? "bg-cyan-500/15 text-cyan-300" :
                    c.tint === "emerald" ? "bg-emerald-500/15 text-emerald-300" :
                    "bg-indigo-500/15 text-indigo-300"
                  }`}>
                    <c.icon size={22} />
                  </div>
                  <h3 className="font-display text-xl text-white">{c.title}</h3>
                  <p className="mt-2 text-sm text-[#9AA4B2] leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* -------- SECURITY -------- */}
      <section id="compliance" className="py-24" data-testid="landing-security">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-2">
              <Reveal>
                <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300">Enterprise security</div>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white leading-tight tracking-tight">
                  Built for the way your auditors think.
                </h2>
                <p className="mt-4 text-[#9AA4B2] leading-relaxed">
                  We treat your procedures the way regulators expect us to — with signed changes, retained history, and role-scoped access at every layer.
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-3 grid sm:grid-cols-2 gap-3">
              {[
                { icon: Fingerprint, title: "Microsoft Entra ID ready", desc: "SSO drop-in via Entra / Azure AD." },
                { icon: Lock, title: "Secure authentication", desc: "JWT with httpOnly cookies + bcrypt hashing." },
                { icon: ScrollText, title: "Immutable audit logs", desc: "Every signature carries name, time, IP and device." },
                { icon: History, title: "Full version history", desc: "Every revision retained and browsable." },
                { icon: KeyRound, title: "Role-based access", desc: "Four permission tiers, folder-scoped visibility." },
                { icon: ServerCog, title: "Enterprise architecture", desc: "SharePoint bridge, HRMS sync, iframe embed." },
              ].map((c, i) => (
                <Reveal key={c.title} delay={i * 50}>
                  <div className="glass rounded-2xl p-4 h-full">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 shrink-0 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-cyan-300">
                        <c.icon size={16} />
                      </div>
                      <div>
                        <div className="text-white font-medium">{c.title}</div>
                        <div className="text-xs text-[#9AA4B2] mt-0.5 leading-relaxed">{c.desc}</div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* -------- FINAL CTA -------- */}
      <section className="py-24" data-testid="landing-cta">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden glass-strong rounded-[36px] p-10 md:p-16 text-center">
              <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#2563EB]/50 blur-[120px]" aria-hidden />
              <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-[#06B6D4]/40 blur-[130px]" aria-hidden />
              <div className="absolute inset-0 -z-0 opacity-30 bg-[radial-gradient(circle_at_center,rgba(79,70,229,0.15),transparent_60%)]" aria-hidden />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300">Get started</div>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-white leading-tight tracking-tight max-w-3xl mx-auto">
                  Ready to modernize your SOP management?
                </h2>
                <p className="mt-4 text-[#9AA4B2] max-w-2xl mx-auto">
                  Consolidate your procedures, prove compliance to your auditors and give your team a source of truth they'll actually use — in an afternoon.
                </p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <button
                    onClick={() => navigate("/signin")}
                    data-testid="cta-signin-btn"
                    className="btn-primary-glow rounded-full px-7 py-3.5 text-base font-medium flex items-center gap-2"
                  >
                    Sign In <ArrowRight size={16} />
                  </button>
                  <a
                    href="#features"
                    className="btn-ghost-glass rounded-full px-7 py-3.5 text-base font-medium"
                  >
                    Explore features
                  </a>
                </div>

                {/* Book a Call with HabileCo */}
                <div className="mt-12 pt-10 border-t border-white/5 max-w-3xl mx-auto">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-xs text-indigo-300 mb-4">
                    <Sparkles size={12} /> Consult with our Experts
                  </div>
                  <h3 className="font-display text-xl font-medium text-white">Book a Call with HabileCo</h3>
                  <p className="mt-2 text-sm text-[#9AA4B2] max-w-lg mx-auto">
                    Let our enterprise architects help design, configure, and migrate your existing SOPs to SOPFlow.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-4 justify-center items-center">
                    <a
                      href="mailto:biz@habileco.com?subject=Book%20a%20Call%20-%20SOPFlow"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-sm font-medium text-white transition hover:text-cyan-300"
                    >
                      <Mail size={14} className="text-cyan-300 animate-pulse" />
                      biz@habileco.com
                    </a>
                    <a
                      href="tel:+917420883406"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-sm font-medium text-white transition hover:text-cyan-300"
                    >
                      <Phone size={14} className="text-cyan-300" />
                      +91 742 088 3406
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* -------- PRICING PLANS -------- */}
      <section id="pricing" className="py-24 border-t border-white/5" data-testid="landing-pricing">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-300">Predictable plans</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white leading-tight tracking-tight">
                Simple flat-rate pricing. Cancel any time.
              </h2>
              <p className="mt-4 text-[#9AA4B2] text-sm leading-relaxed">
                Start free, then unlock connectors, IoT triggers, and WhatsApp alerts when you need them.
              </p>
            </div>
          </Reveal>

          <PricingPanel onCta={() => navigate("/signin")} />
        </div>
      </section>

      {/* -------- FOOTER -------- */}
      <footer className="pt-10 pb-12 border-t border-white/5" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#2563EB] via-[#4F46E5] to-[#06B6D4] flex items-center justify-center">
                  <Sparkles size={16} className="text-white" strokeWidth={2.2} />
                </div>
                <span className="font-display font-bold text-lg text-white">SOPFlow</span>
              </div>
              <p className="mt-3 text-sm text-[#9AA4B2] max-w-xs">
                A calmer, auditable home for every procedure your team runs on.
              </p>
              <a
                href="https://www.linkedin.com/company/habile-co/"
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-xs text-[#9AA4B2] hover:text-white transition-colors"
                data-testid="footer-linkedin"
              >
                <LinkedinIcon size={14} /> LinkedIn
              </a>
            </div>

            {[
              { title: "Product", links: [["Features", "#features"], ["Pricing", "#pricing"], ["Solutions", "#solutions"], ["Compliance", "#compliance"]] },
              { title: "Company", links: [["About", "#about"], ["Contact", "mailto:biz@habileco.com"], ["LinkedIn", "https://www.linkedin.com/company/habile-co/"]] },
              { title: "Legal", links: [["Security", "#compliance"], ["Privacy", "#"], ["Terms", "#"]] },
            ].map((col) => (
              <div key={col.title}>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#5B6472]">{col.title}</div>
                <ul className="mt-4 space-y-2">
                  {col.links.map(([label, href]) => (
                    <li key={label}>
                      <a 
                        href={href} 
                        {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="text-sm text-[#9AA4B2] hover:text-white transition"
                      >
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5B6472]">
            <div>
              © {new Date().getFullYear()} SOPFlow. All rights reserved. Built with precision by{" "}
              <a 
                href="https://habileco.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[#9AA4B2] hover:text-white underline decoration-cyan-300/40 underline-offset-4 transition"
              >
                HabileCo
              </a>.
            </div>
            <div className="flex gap-4">
              <a href="#" className="hover:text-white">Privacy</a>
              <a href="#" className="hover:text-white">Terms</a>
              <a href="mailto:biz@habileco.com" className="hover:text-white">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
